import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "@iconify/react";

import {
  fetchFrasesPlantelThunk,
  createFrasePlantelThunk,
  updateFrasePlantelThunk,
  toggleFrasePlantelActiveThunk,
} from "../../store/preguntasTalento/preguntasTalentoPlantel.thunks";
import { fetchTiposFortalezaThunk } from "../../store/preguntasTalento/preguntasTalento.thunks";

import Alert from "../UI/Alert";
import Loader from "../UI/Loader";
import EmptyState from "../UI/EmptyState";
import Pagination from "../UI/Pagination";
import SearchInput from "../UI/SearchInput";
import Modal from "../UI/Modal";
import Select2 from "../UI/Select2";

const emptyForm = { texto: "", orden: 1, tipo_fortaleza: "", is_active: true };
const PAGE_SIZE = 10;

const FrasesPlantelLayer = ({ plantelId }) => {
  const dispatch = useDispatch();
  const { data, loading, error } = useSelector((s) => s.preguntasTalentoPlantel.frases);
  const tipos = useSelector((s) => s.preguntasTalento.tiposFortaleza);

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filterTipo, setFilterTipo] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const [confirmToggle, setConfirmToggle] = useState(null);

  useEffect(() => { dispatch(fetchTiposFortalezaThunk()); }, [dispatch]);

  useEffect(() => {
    if (!plantelId) return;
    dispatch(fetchFrasesPlantelThunk({
      page,
      search,
      plantel: plantelId,
      tipo_fortaleza: filterTipo || undefined,
    }));
  }, [dispatch, plantelId, page, search, filterTipo]);

  useEffect(() => { setPage(1); }, [search, filterTipo]);

  const fetchTiposLocal = useMemo(
    () => (q = "") => {
      const items = tipos.map((t) => ({ id: String(t.id), label: t.label }));
      if (!q) return Promise.resolve(items);
      const qq = q.toLowerCase();
      return Promise.resolve(items.filter((i) => i.label.toLowerCase().includes(qq)));
    },
    [tipos]
  );

  const refetch = () => {
    dispatch(fetchFrasesPlantelThunk({
      page, search, plantel: plantelId,
      tipo_fortaleza: filterTipo || undefined,
    }));
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      texto: row.texto,
      orden: row.orden,
      tipo_fortaleza: row.tipo_fortaleza ? String(row.tipo_fortaleza) : "",
      is_active: row.is_active,
    });
    setFormError(null);
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setSaving(true);
    const payload = {
      plantel: Number(plantelId),
      texto: form.texto,
      orden: Number(form.orden) || 1,
      tipo_fortaleza: form.tipo_fortaleza ? Number(form.tipo_fortaleza) : null,
      is_active: form.is_active,
    };
    const action = editing
      ? updateFrasePlantelThunk({ id: editing.id, payload })
      : createFrasePlantelThunk(payload);
    const result = await dispatch(action);
    setSaving(false);
    if (result.meta.requestStatus === "fulfilled") {
      setModalOpen(false);
      refetch();
    } else {
      setFormError(result.payload || "No se pudo guardar");
    }
  };

  const handleToggle = async () => {
    const row = confirmToggle;
    setConfirmToggle(null);
    await dispatch(toggleFrasePlantelActiveThunk(row.id));
    refetch();
  };

  return (
    <div className="card p-0 radius-12">
      <div className="card-body p-24">
        <div className="mb-3">
          <div className="row g-2 align-items-center">
            <div className="col-12 col-md-auto">
              <h5 className="mb-0 fw-semibold d-flex align-items-center gap-2">
                <Icon icon="mdi:message-text-outline" width="22" />
                Fortaleza
              </h5>
            </div>
            <div className="col-12 col-md">
              <div className="d-flex flex-column flex-sm-row gap-2 justify-content-md-end">
                <div style={{ minWidth: 220 }}>
                  <Select2
                    value={filterTipo}
                    onChange={setFilterTipo}
                    fetchOptions={fetchTiposLocal}
                    placeholder="Filtrar por fortaleza"
                  />
                </div>
                <SearchInput
                  value={search}
                  onChange={setSearch}
                  placeholder="Buscar frases..."
                  minChars={3}
                />
                <button
                  type="button"
                  className="btn btn-primary d-flex align-items-center gap-1"
                  onClick={openCreate}
                >
                  <Icon icon="mdi:plus" width="18" />
                  <span>Nueva frase</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {error && <Alert message={String(error)} variant="error" />}

        {loading ? (
          <Loader center text="Cargando frases..." delay={200} />
        ) : data.results.length === 0 ? (
          <EmptyState
            icon={search ? "mdi:magnify-remove-outline" : "mdi:message-text-outline"}
            title={search ? "No se encontraron frases" : "Aún no hay frases registradas"}
            description={search ? "Intenta con otra búsqueda" : "Dale click a \"Nueva frase\" para crear la primera"}
          />
        ) : (
          <>
            <div className="table-responsive scroll-sm">
              <table className="table bordered-table mb-0" style={{ minWidth: 800 }}>
                <thead>
                  <tr>
                    <th scope="col" style={{ minWidth: 60 }}>ID</th>
                    <th scope="col" style={{ minWidth: 300 }}>Texto</th>
                    <th scope="col" style={{ minWidth: 160 }}>Fortaleza</th>
                    <th scope="col" style={{ minWidth: 80 }}>Orden</th>
                    <th scope="col" style={{ minWidth: 100 }}>Estado</th>
                    <th scope="col" style={{ minWidth: 160 }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {data.results.map((row) => (
                    <tr key={row.id} className={!row.is_active ? "text-secondary-light" : ""}>
                      <td>{row.id}</td>
                      <td>{row.texto}</td>
                      <td>{row.tipo_fortaleza_label || "—"}</td>
                      <td>{row.orden}</td>
                      <td>
                        <span className={`badge ${row.is_active ? "bg-success-focus text-success-main" : "bg-neutral-200 text-secondary-light"}`}>
                          {row.is_active ? "Alta" : "Anulada"}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                            onClick={() => openEdit(row)}
                          >
                            <Icon icon="mdi:pencil-outline" width="16" />
                            Editar
                          </button>
                          <button
                            type="button"
                            className={`btn btn-sm d-flex align-items-center gap-1 ${
                              row.is_active ? "btn-outline-danger" : "btn-outline-success"
                            }`}
                            onClick={() => setConfirmToggle(row)}
                          >
                            <Icon icon={row.is_active ? "mdi:close-circle-outline" : "mdi:check-circle-outline"} width="16" />
                            {row.is_active ? "Anular" : "Reactivar"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={page}
              count={data.count}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          </>
        )}
      </div>

      <Modal
        open={modalOpen}
        onClose={() => !saving && setModalOpen(false)}
        title={editing ? "Editar frase" : "Alta de frase"}
        size="lg"
      >
        {saving ? (
          <Loader center type="dots" text="Guardando..." />
        ) : (
          <form onSubmit={handleSubmit}>
            {formError && <Alert message={String(formError)} variant="error" />}

            <div className="mb-3">
              <Select2
                label="Tipo de fortaleza"
                required
                value={form.tipo_fortaleza}
                onChange={(v) => setForm({ ...form, tipo_fortaleza: v })}
                fetchOptions={fetchTiposLocal}
                placeholder="Selecciona la fortaleza"
              />
            </div>

            <div className="mb-3">
              <label className="form-label fw-medium">
                Texto <span className="text-danger">*</span>
              </label>
              <textarea
                className="form-control"
                rows={3}
                maxLength={500}
                required
                value={form.texto}
                onChange={(e) => setForm({ ...form, texto: e.target.value })}
                placeholder="Texto de la frase"
              />
            </div>

            <div className="mb-3">
              <label className="form-label fw-medium">Orden</label>
              <input
                type="number"
                min={1}
                className="form-control"
                value={form.orden}
                onChange={(e) => setForm({ ...form, orden: Number(e.target.value) || 1 })}
              />
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setModalOpen(false)}
              >
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary">
                {editing ? "Guardar cambios" : "Dar de alta"}
              </button>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        open={!!confirmToggle}
        onClose={() => setConfirmToggle(null)}
        title={confirmToggle?.is_active ? "Anular frase" : "Reactivar frase"}
        size="sm"
      >
        <p className="text-secondary-light">
          ¿Seguro que quieres {confirmToggle?.is_active ? "anular" : "reactivar"} la frase{" "}
          <strong className="text-primary">{confirmToggle?.texto}</strong>?
        </p>
        <div className="d-flex justify-content-end gap-2 mt-3">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setConfirmToggle(null)}
          >
            Cancelar
          </button>
          <button
            type="button"
            className={`btn ${confirmToggle?.is_active ? "btn-danger" : "btn-success"}`}
            onClick={handleToggle}
          >
            {confirmToggle?.is_active ? "Anular" : "Reactivar"}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default FrasesPlantelLayer;
